import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { Field, inputCls, SelectField } from "./FormFields";
import { STATES, EXPERIENCE, INVESTMENT } from "./data";
import { EASE } from "./Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { full_name: "", business_name: "", mobile: "", email: "", city_district: "", state: "", experience: "", investment_capacity: "", storage_capacity: "", message: "" };
const REQUIRED = { full_name: "Full name", business_name: "Firm / business name", mobile: "Mobile number", city_district: "City & district", state: "State", experience: "Business experience", investment_capacity: "Investment capacity" };

const validate = (f) => {
  const e = {};
  Object.entries(REQUIRED).forEach(([k, label]) => { if (!f[k].trim()) e[k] = `${label} is required`; });
  if (f.mobile && !/^(\+?91)?[6-9]\d{9}$/.test(f.mobile.replace(/[\s-]/g, ""))) e.mobile = "Enter a valid 10-digit mobile number";
  if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Enter a valid email address";
  return e;
};

const Success = ({ onReset }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: EASE }} data-testid="distributor-success-state" className="flex min-h-[520px] flex-col items-center justify-center text-center">
    <span className="grid h-20 w-20 place-items-center rounded-full bg-ns-emerald text-ns-gold"><CheckCircle2 className="h-9 w-9" /></span>
    <h3 className="mt-8 font-serif text-4xl font-semibold text-ns-emerald">Application received</h3>
    <p data-testid="distributor-success-message" className="mt-4 max-w-sm text-base leading-relaxed text-ns-muted">
      Thank you for your interest. Your application is safely with our team. We’ll connect with you soon.
    </p>
    <button onClick={onReset} data-testid="distributor-submit-another-button" className="btn-outline mt-10">Submit another application</button>
  </motion.div>
);

export const DistributorForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length) { toast.error("Please complete the highlighted fields."); return; }
    setLoading(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || null]));
      await axios.post(`${API}/distributor-applications`, payload);
      toast.success("Application submitted. Our team will reach out soon.");
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg ?? "Submission failed." : "Could not submit right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setForm(EMPTY); setErrors({}); setDone(false); };

  if (done) return <Success onReset={reset} />;

  const text = (k, label, opts = {}) => (
    <Field label={label} required={!!REQUIRED[k]} error={errors[k]} htmlFor={k}>
      <input id={k} value={form[k]} onChange={set(k)} data-testid={`distributor-input-${k.replace(/_/g, "-")}`} className={inputCls(errors[k])} {...opts} />
    </Field>
  );

  return (
    <form onSubmit={submit} noValidate data-testid="distributor-form">
      <h3 data-testid="distributor-form-title" className="font-serif text-3xl font-semibold text-ns-emerald sm:text-4xl">Distributor / Retailer Application</h3>
      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        {text("full_name", "Full Name", { placeholder: "Your full name", autoComplete: "name" })}
        {text("business_name", "Firm / Business Name", { placeholder: "Business name" })}
        {text("mobile", "Mobile / WhatsApp Number", { type: "tel", placeholder: "10-digit mobile", inputMode: "tel", autoComplete: "tel" })}
        {text("email", "Email Address", { type: "email", placeholder: "you@business.com", autoComplete: "email" })}
        {text("city_district", "City & District", { placeholder: "e.g. Bettiah, West Champaran" })}
        <Field label="State" required error={errors.state} htmlFor="state">
          <SelectField id="state" value={form.state} onChange={set("state")} options={STATES} placeholder="Select state" error={errors.state} testId="distributor-select-state" />
        </Field>
        <Field label="Existing Business / Distribution Experience" required error={errors.experience} htmlFor="experience">
          <SelectField id="experience" value={form.experience} onChange={set("experience")} options={EXPERIENCE} placeholder="Select experience" error={errors.experience} testId="distributor-select-experience" />
        </Field>
        <Field label="Investment Capacity" required error={errors.investment_capacity} htmlFor="investment_capacity">
          <SelectField id="investment_capacity" value={form.investment_capacity} onChange={set("investment_capacity")} options={INVESTMENT} placeholder="Select range" error={errors.investment_capacity} testId="distributor-select-investment" />
        </Field>
        <div className="sm:col-span-2">{text("storage_capacity", "Storage / Godown Capacity", { placeholder: "e.g. 500 sq ft godown" })}</div>
        <div className="sm:col-span-2">
          <Field label="Message / Query" htmlFor="message">
            <textarea id="message" rows={4} value={form.message} onChange={set("message")} data-testid="distributor-input-message" placeholder="Tell us about your business" className={`${inputCls(false)} h-auto py-3`} />
          </Field>
        </div>
      </div>
      <button type="submit" disabled={loading} data-testid="distributor-submit-button" className="btn-primary group mt-8 w-full justify-center disabled:opacity-70">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        {loading ? "Submitting..." : "Submit Application"}
        {!loading && <ArrowRight className="h-4 w-4 text-ns-gold transition-transform duration-500 group-hover:translate-x-1" />}
      </button>
    </form>
  );
};
